import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, Calendar, ArrowLeft, 
  Phone, Mail
} from 'lucide-react';
import { useProjet, useProjetGalerie, useProjetStatistiques, useProjetTypesMaisons } from '../hooks/usePublicApi';
import { useTexts } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import MapView from '../components/common/MapView';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('types');
  const [selectedImage, setSelectedImage] = useState(0);

  // Initialiser Fancybox
  useEffect(() => {
    Fancybox.bind('[data-fancybox]');

    return () => {
      Fancybox.unbind('[data-fancybox]');
      Fancybox.close();
    };
  }, []);

  // Récupération des données depuis l'API
  const { data: project, loading: projectLoading, error: projectError } = useProjet(id ? parseInt(id) : null);
  const { data: galerie } = useProjetGalerie(id ? parseInt(id) : null);
  const { loading: statsLoading } = useProjetStatistiques(id ? parseInt(id) : null);
  const { data: typesMaisons, loading: typesLoading } = useProjetTypesMaisons(id ? parseInt(id) : null);
  const { getText } = useTexts();

  // Gestion des états de chargement et d'erreur
  if (projectLoading || statsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ErrorMessage 
            error={projectError || 'Projet non trouvé'} 
            onDismiss={() => window.history.back()} 
          />
          <Link to="/projects" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            {getText('project.detail.back_to_projects')}
          </Link>
        </div>
      </div>
    );
  }

  // Utiliser les statistiques calculées côté serveur
  // const totalHouses = stats?.total_logements || 0;
  // const availableHouses = stats?.logements_disponibles || 0;
  // const tauxOccupation = stats?.taux_occupation || 0;

  const tabs = [
    { id: 'types', label: 'Vue d\'ensemble' },
    { id: 'gallery', label: 'Galerie' },
    { id: 'location', label: 'Localisation' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            <Link
              to="/projects"
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {getText('project.detail.back_to_projects')}
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center" 
        style={{ 
          backgroundImage: `url(${project.image_principale_url || 'https://demo.sogeimco.com/static/images/default-project.svg'})` 
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-8">
          <div className="text-white">
            <div className="mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                project.statut_projet === 'EN_COURS' ? 'bg-green-500 text-white' :
                project.statut_projet === 'A_VENIR' ? 'bg-blue-500 text-white' :
                'bg-gray-500 text-white'
              }`}>
                {project.statut_projet.replace('_', ' ')}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {project.nom_projet}
            </h1>
            <div className="flex items-center mb-2">
              <MapPin className="w-5 h-5 mr-2" />
              <span className="text-lg">{project.adresse}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span>Livraison prévue: {new Date(project.date_fin_prevue).getFullYear()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'types' && (
              <div className="space-y-6">
                {/* Description */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <p className="text-gray-600 leading-relaxed">
                    {project.description}
                  </p>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Types de biens disponibles</h2>
                  
                  {typesLoading ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : typesMaisons && typesMaisons.length > 0 ? (
                    <div className="space-y-6">
                      {typesMaisons.map((typeMaison: any) => {
                        const formatCurrency = (amount: string | number) => {
                          const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
                          return new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'XOF',
                            minimumFractionDigits: 0,
                          }).format(numAmount);
                        };

                        return (
                          <div key={typeMaison.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                            {/* Header */}
                            <h3 className="text-xl font-bold text-gray-900 mb-4">
                              {typeMaison.type_bien} {typeMaison.type_maison_designation?.toUpperCase()} – {typeMaison.surface_totale} m²
                            </h3>
                            
                            <div className="flex flex-col md:flex-row gap-6">
                              {/* Contenu - à gauche */}
                              <div className="flex-1">
                                {/* Détails principaux */}
                                <div className="space-y-3">
                                  <div>
                                    <span className="text-sm font-medium text-gray-700 block mb-1">Prix</span>
                                    <span className="text-lg font-bold text-blue-600">
                                      {formatCurrency(typeMaison.prix_unitaire_ht || 0)}
                                    </span>
                                  </div>
                                  
                                  {typeMaison.frais_dossier && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 block mb-1">Frais de dossier</span>
                                      <span className="text-base font-semibold text-gray-900">
                                        {formatCurrency(typeMaison.frais_dossier)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {typeMaison.apport_initial && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 block mb-1">Apport initial</span>
                                      <span className="text-base font-semibold text-gray-900">
                                        {formatCurrency(typeMaison.apport_initial)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {typeMaison.modalites_paiement_avant_livraison && typeMaison.duree_avant_livraison_mois && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 block mb-1">Mensualité avant la livraison du bien</span>
                                      <span className="text-base font-semibold text-gray-900">
                                        {formatCurrency(typeMaison.modalites_paiement_avant_livraison)} / {typeMaison.duree_avant_livraison_mois} mois
                                      </span>
                                    </div>
                                  )}
                                  
                                  {typeMaison.modalites_paiement_apres_livraison && typeMaison.duree_apres_livraison_mois && (
                                    <div>
                                      <span className="text-sm font-medium text-gray-700 block mb-1">Mensualité après la livraison du bien</span>
                                      <span className="text-base font-semibold text-gray-900">
                                        {formatCurrency(typeMaison.modalites_paiement_apres_livraison)} / {typeMaison.duree_apres_livraison_mois} mois
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Image - à droite */}
                              {typeMaison.image_url_final && (
                                <div className="md:w-1/2 lg:w-2/5 flex-shrink-0">
                                  <a
                                    href={typeMaison.image_url_final}
                                    data-fancybox="gallery"
                                    data-caption={`${typeMaison.type_bien} ${typeMaison.type_maison_designation} - ${typeMaison.surface_totale} m²`}
                                  >
                                    <img 
                                      src={typeMaison.image_url_final} 
                                      alt={`${typeMaison.type_bien} ${typeMaison.type_maison_designation}`}
                                      className="w-full h-auto min-h-[200px] md:min-h-[300px] object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                                    />
                                  </a>
                                </div>
                              )}
                            </div>

                            {/* Footer */}
                            <div className="border-t pt-4 mt-4 flex items-center justify-between">
                              <span className="text-sm text-gray-600">
                                Bien disponible : {typeMaison.places_disponibles ?? typeMaison.nombre_maisons_niveau ?? 0}
                              </span>
                              <Link
                                to={`/contact?projet=${project?.id}&projet_nom=${encodeURIComponent(project?.nom_projet || '')}&type_maison=${encodeURIComponent(typeMaison.type_maison_designation)}`}
                                className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                              >
                                Intéressé
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg mb-2">Aucun type de bien disponible</p>
                      <p className="text-sm text-gray-400">
                        Les types de biens pour ce projet seront bientôt disponibles
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Galerie médias</h2>
                  
                  {galerie && galerie.length > 0 ? (
                    <>
                      {/* Main Media Display */}
                      <div className="mb-6">
                        <div className="relative h-[600px] md:h-[700px] rounded-lg overflow-hidden bg-gray-100">
                          {selectedImage < galerie.length && (() => {
                            const currentMedia = galerie[selectedImage];
                            const isVideo = currentMedia.type_media === 'VIDEO';
                            
                            return isVideo ? (
                              <video
                                controls
                                className="w-full h-full object-cover"
                                poster={currentMedia.image_url || undefined}
                              >
                                <source src={currentMedia.chemin_fichier} type="video/mp4" />
                                Votre navigateur ne supporte pas la lecture de vidéos.
                              </video>
                            ) : (
                              <img
                                src={currentMedia.chemin_fichier || 'https://demo.sogeimco.com/static/images/default-project.svg'}
                                alt={currentMedia.nom_fichier || 'Média'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = 'https://demo.sogeimco.com/static/images/default-project.svg';
                                }}
                              />
                            );
                          })()}
                          
                          <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                            {selectedImage + 1} / {galerie.length}
                          </div>
                          
                          {selectedImage < galerie.length && (
                            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg text-sm">
                              {galerie[selectedImage].nom_fichier}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Media Thumbnails */}
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                        {galerie.map((media, index) => (
                          <button
                            key={media.id}
                            onClick={() => setSelectedImage(index)}
                            className={`relative h-24 md:h-32 rounded-lg overflow-hidden group ${
                              selectedImage === index ? 'ring-2 ring-blue-500' : ''
                            }`}
                          >
                            {media.type_media === 'VIDEO' ? (
                              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                <div className="text-white text-center">
                                  <div className="text-xs font-medium">📹</div>
                                  <div className="text-xs opacity-75">VIDEO</div>
                                </div>
                              </div>
                            ) : media.type_media === 'PLAN_3D' ? (
                              <div className="w-full h-full bg-blue-800 flex items-center justify-center">
                                <div className="text-white text-center">
                                  <div className="text-xs font-medium">📐</div>
                                  <div className="text-xs opacity-75">3D</div>
                                </div>
                              </div>
                            ) : media.type_media === 'VUE_360' ? (
                              <div className="w-full h-full bg-green-800 flex items-center justify-center">
                                <div className="text-white text-center">
                                  <div className="text-xs font-medium">🌐</div>
                                  <div className="text-xs opacity-75">360°</div>
                                </div>
                              </div>
                            ) : (
                              <img
                                src={media.chemin_fichier || 'https://demo.sogeimco.com/static/images/default-project.svg'}
                                alt={media.nom_fichier || 'Image'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = 'https://demo.sogeimco.com/static/images/default-project.svg';
                                }}
                              />
                            )}
                            
                            {/* Media type badge */}
                            <div className="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center">
                              {media.type_media === 'PHOTO' ? '📷' : 
                               media.type_media === 'VIDEO' ? '🎥' :
                               media.type_media === 'PLAN_3D' ? '📐' : '🌐'}
                            </div>
                            
                            {/* Principal badge */}
                            {media.principal && (
                              <div className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1 py-0.5 rounded">
                                ⭐
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-gray-400 text-6xl mb-4">📷</div>
                      <p className="text-gray-500 text-lg mb-2">Aucun média disponible</p>
                      <p className="text-sm text-gray-400">
                        Les médias de ce projet seront bientôt disponibles
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}


            {activeTab === 'location' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Localisation</h2>
                  <div className="mb-6">
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>{project.adresse}</span>
                    </div>
                    <MapView
                      latitude={parseFloat(project.coordonnees_gps_lat)}
                      longitude={parseFloat(project.coordonnees_gps_lng)}
                      address={project.adresse}
                      zoom={15}
                      height="h-96"
                    />
                    <div className="mt-4 text-sm text-gray-500">
                      <p>📍 Coordonnées GPS: {project.coordonnees_gps_lat}, {project.coordonnees_gps_lng}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="lg:sticky lg:top-24">
              {/* Contact Card */}
              <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{getText('project.detail.interested_title')}</h3>
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-blue-600 mr-3" />
                  <span className="text-sm text-gray-600">
                    {getText('contact.info.phone_value')}
                  </span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-blue-600 mr-3" />
                  <span className="text-sm text-gray-600">
                    {getText('contact.info.email_value')}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <Link
                  to={`/contact?projet=${project?.id}&projet_nom=${encodeURIComponent(project?.nom_projet || '')}`}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-center block"
                >
                  {getText('project.detail.ask_information')}
                </Link>
                <Link
                  to="/contact"
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-center block"
                >
                  {getText('project.detail.contact_us')}
                </Link>
              </div>
            </div>

            {/* Availability */}
            {/* <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{getText('project.detail.availability')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{getText('project.detail.available_houses')}</span>
                  <span className="font-semibold text-green-600">{availableHouses}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{getText('project.detail.total_houses')}</span>
                  <span className="font-semibold">{totalHouses}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${((totalHouses - availableHouses) / totalHouses) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {tauxOccupation}% de logements occupés
                </p>
              </div>
            </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}